import { useEffect, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { useProject } from "../context/ProjectContext";
import { useAuth } from "../context/AuthContext";
import { FileText, FolderOpen, CaretDown, CaretRight } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import PreviewPane from "../components/docs/PreviewPane";
import StatusPane from "../components/docs/StatusPane";
import DmsBrowser from "../components/DmsBrowser";
import DocRevisions from "../components/docs/DocRevisions";

const STAGES = [
  { stage: 4, label: "Stage 4 · Design Sheet" },
  { stage: 5, label: "Stage 5 · Cross-Functional Handover" },
  { stage: 6, label: "Stage 6 · DO Notes to Teams" },
  { stage: 7, label: "Stage 7 · Client Approval Package" },
];

export default function Documents({ embedded = false }) {
  const { user } = useAuth();
  const { projects, pid, setPid } = useProject();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [openStages, setOpenStages] = useState({ 4: true, 5: true, 6: true, 7: true });
  const [busy, setBusy] = useState("");
  const [revKey, setRevKey] = useState(0);
  const [mode, setMode] = useState("generation"); // generation | browser

  const load = (id = pid) => {
    if (!id) return;
    api.get(`/documents/${id}`).then(({ data }) => setData(data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => { setSelected(null); load(pid); }, [pid]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); load(); setRevKey((k) => k + 1); }
    catch (e) { toast.error(fmtErr(e), { duration: 7000 }); }
    finally { setBusy(""); }
  };

  const treeItems = (stage) => {
    if (!data) return [];
    if (stage === 4) {
      return [
        { key: "design-sheet", label: `Design Sheet${data.design_sheet ? ` · Rev ${data.design_sheet.rev}` : ""}`, badge: data.design_sheet?.status },
        { key: "design-output-notes", label: "Design Output Notes", badge: data.design_output_notes ? "generated" : null },
      ];
    }
    if (stage === 5) {
      return (data.handover_packs || []).map((p) => ({
        key: `pack-${p.id}`, label: p.pack_name, badge: p.status, pack: p,
      }));
    }
    if (stage === 6) {
      return (data.do_notes || []).map((n) => ({
        key: `note-${n.id}`, label: n.title, badge: `${n.reads.length}/${n.sent_to.length} read`, note: n,
      }));
    }
    if (stage === 7) {
      return (data.approval_package?.docs || []).map((d) => ({
        key: `doc-${d.id}`, label: d.title, badge: `Rev ${data.approval_package.rev}`, doc: d,
      }));
    }
    return [];
  };

  return (
    <div className={embedded ? "" : "p-6"} data-testid="documents-page">
      <div className="flex items-center gap-4 flex-wrap">
        {!embedded && (
          <>
            <div>
              <h2 className="text-base font-medium text-[var(--c-text)]">Documents</h2>
              <p className="text-xs text-[var(--c-muted)] mt-0.5">Design sheet → handover → DO notes → client approval</p>
            </div>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="docs-project-select" className="w-96 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
                <SelectValue placeholder="Select project…" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
                    <span className="font-mono text-[var(--c-muted)] mr-2">{p.project_code}</span>{p.product_summary}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        {busy && <span className="font-mono text-[10px] text-[var(--c-accent)] tracking-widest" data-testid="docs-busy">WORKING…</span>}
        <div className="flex gap-1 ml-auto" data-testid="docs-mode-toggle">
          {[["generation", "📝 Generation"], ["browser", "📁 File Browser"]].map(([m, l]) => (
            <button key={m} data-testid={`docs-mode-${m}`} onClick={() => setMode(m)}
              className={`h-8 px-3 text-[11px] rounded-sm border transition-colors ${
                mode === m ? "bg-[var(--c-accent)]/15 border-[var(--c-accent)]/60 text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {mode === "browser" && pid && <div className="mt-5"><DmsBrowser pid={pid} /></div>}
      {mode === "browser" && !pid && (
        <div className="mt-10 text-center text-[12px] text-[var(--c-muted)]">Select a project to browse its files.</div>
      )}

      {mode === "generation" && data && !data.has_design_results && (
        <div className="mt-5 bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm p-5 text-sm text-[var(--c-text)]" data-testid="no-design-note">
          Run a calculation in the Design Engine first — documents are auto-populated from design results.
        </div>
      )}

      {mode === "generation" && data && (
        <div className="flex gap-4 mt-5 items-start">
          <div className="w-64 shrink-0 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid="doc-tree">
            {STAGES.map((s) => (
              <div key={s.stage} className="border-b border-[var(--c-border)]/50 last:border-0">
                <button className="w-full flex items-center gap-2 px-3 py-2.5 text-[11px] font-medium text-[var(--c-text)] hover:bg-[var(--c-surface-2)]/70"
                  data-testid={`tree-stage-${s.stage}`}
                  onClick={() => setOpenStages((o) => ({ ...o, [s.stage]: !o[s.stage] }))}>
                  {openStages[s.stage] ? <CaretDown size={11} /> : <CaretRight size={11} />}
                  <FolderOpen size={13} className="text-[var(--c-amber)]" /> {s.label}
                </button>
                {openStages[s.stage] && (
                  <div className="pb-1.5">
                    {treeItems(s.stage).map((item) => (
                      <button key={item.key} data-testid={`tree-node-${item.key}`}
                        onClick={() => setSelected({ stage: s.stage, ...item })}
                        className={`w-full flex items-center gap-2 pl-8 pr-3 py-1.5 text-[11px] text-left transition-colors duration-100 ${
                          selected?.key === item.key ? "bg-[var(--c-accent)]/15 text-[var(--c-text)]" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
                        <FileText size={11} className="shrink-0" />
                        <span className="truncate flex-1">{item.label}</span>
                        {item.badge && <span className="font-mono text-[8px] uppercase tracking-wider text-[var(--c-muted)] shrink-0">{item.badge}</span>}
                      </button>
                    ))}
                    {treeItems(s.stage).length === 0 && (
                      <div className="pl-8 pr-3 py-1.5 text-[10px] text-[var(--c-muted)] font-mono">— not generated —</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex-1 min-w-0">
            <PreviewPane selected={selected} data={data} />
          </div>

          <div className="w-80 shrink-0">
            <StatusPane stage={selected?.stage} data={data} pid={pid} user={user} busy={busy} run={run} />
            <DocRevisions pid={pid} refreshKey={revKey} />
          </div>
        </div>
      )}
    </div>
  );
}
